import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    type ChartOptions,
    type Plugin,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import { Info } from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';
import { useTheme } from '@/contexts/ThemeContext';
import { formatLrAsymmetryPct } from '@/utils/hawkinAsymmetry';

// Source: Hawkin Dynamics Metric Database (hawkindynamics.com/hawkin-metric-database)
const METRIC_TOOLTIPS: Record<string, { fullName: string; description: string; link?: string }> = {
    'Jump Height': { fullName: 'Jump Height', description: 'Change in center of mass position between takeoff and peak vertical displacement, calculated using takeoff velocity and equations of uniformly accelerated motion.' },
    'Peak Force': { fullName: 'Peak Propulsive Force', description: 'Peak instantaneous vertical ground reaction force applied to the center of mass during the propulsion phase.' },
    'RSI': { fullName: 'Reactive Strength Index', description: 'Flight time divided by total time from movement initiation to takeoff. Higher values indicate better reactive strength.' },
    'Takeoff Vel.': { fullName: 'Takeoff Velocity', description: 'Vertical velocity of the center of mass at the instant of takeoff.' },
    'Flight Time': { fullName: 'Flight Time', description: 'Time taken to complete the flight phase.' },
    'Peak Power': { fullName: 'Peak Propulsive Power', description: 'Peak instantaneous mechanical power applied to the center of mass during the propulsion phase.' },
    'CM Depth': { fullName: 'Countermovement Depth', description: 'Peak negative vertical displacement of the center of mass during the countermovement.' },
    'Time to Takeoff': { fullName: 'Time to Takeoff', description: 'Total time from the initiation of movement to the instant of takeoff.' },
    'Braking RFD': { fullName: 'Braking Rate of Force Development', description: 'Average slope of the vertical ground reaction force during the braking phase. The braking phase is velocity-defined: it begins at peak downward velocity (≈ when force crosses body weight on the way up) and ends when velocity reaches zero — not at the force minimum/maximum. This is why the highlighted region may appear shifted right from the force curve\'s dip.', link: 'https://www.hawkindynamics.com/blog/phases-of-the-cmj' },
    'L/R Prop. Force': { fullName: 'Left/Right Average Propulsive Force', description: 'Asymmetry between left and right average vertical ground reaction forces during the propulsion phase. Formula: (L−R)/(L+R)×100. Positive = left-dominant (n% L); negative = right-dominant (n% R).' },
    'L/R Braking Force': { fullName: 'Left/Right Average Braking Force', description: 'Asymmetry between left and right average vertical ground reaction forces during the braking phase. Formula: (L−R)/(L+R)×100. Positive = left-dominant (n% L); negative = right-dominant (n% R).' },
    'Peak Landing Force': { fullName: 'Peak Landing Force', description: 'Peak instantaneous vertical ground reaction force applied to the center of mass during the landing phase.' },
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin,
);

interface ForceTimeData {
    test_id: string;
    sample_count: number;
    time_s: number[];
    left_force_n: (number | null)[];
    right_force_n: (number | null)[];
    combined_force_n: (number | null)[];
    has_left: boolean;
    has_right: boolean;
    velocity_m_s: (number | null)[];
    displacement_m: (number | null)[];
    power_w: (number | null)[];
    test_meta: {
        athlete_name: string;
        test_type_name: string;
        segment: string;
        trial_label: string;
        date: string;
        system_weight_n: number | null;
    };
    metrics: Record<string, any>;
}

interface ForceTimeCurveProps {
    testId: string;
    onClose?: () => void;
}

type OverlayChannel = 'velocity' | 'power' | 'displacement';

const OVERLAY_CONFIG: Record<OverlayChannel, { label: string; unit: string; color: string }> = {
    velocity: { label: 'Velocity', unit: 'm/s', color: '#F59E0B' },
    power: { label: 'Power', unit: 'W', color: '#8B5CF6' },
    displacement: { label: 'Displacement', unit: 'm', color: '#14B8A6' },
};

interface PhaseResult {
    eccentricStart: number;
    peakForceIdx: number;
    peakForce: number;
    flightStart: number;
    flightEnd: number;
    landingEnd: number;
}

function findPhases(
    time: number[],
    combined: (number | null)[],
    bodyWeight: number | null,
    timeToTakeoff?: number | null,
    flightTimeSec?: number | null,
): PhaseResult | null {
    if (!bodyWeight || time.length === 0) return null;

    const bw = bodyWeight;
    const nearZero = bw * 0.08;

    // 1) Find flight phase: collect all sustained near-zero stretches, then pick
    //    the one whose duration best matches Hawkin's flight_time_s. This avoids
    //    false positives from aggressive countermovements that momentarily unload
    //    the plates.
    const candidates: { start: number; end: number }[] = [];
    let runStart = -1;
    for (let i = 0; i < combined.length; i++) {
        const f = combined[i];
        if (f == null) continue;
        if (f < nearZero) {
            if (runStart < 0) runStart = i;
        } else {
            if (runStart >= 0 && (i - runStart) > 20) {
                candidates.push({ start: runStart, end: i });
            }
            runStart = -1;
        }
    }
    if (runStart >= 0 && (combined.length - runStart) > 20) {
        candidates.push({ start: runStart, end: combined.length - 1 });
    }

    if (candidates.length === 0) return null;

    let flightStart: number;
    let flightEnd: number;
    if (candidates.length === 1 || !flightTimeSec || flightTimeSec <= 0) {
        flightStart = candidates[0].start;
        flightEnd = candidates[0].end;
    } else {
        let bestIdx = 0;
        let bestDiff = Infinity;
        for (let c = 0; c < candidates.length; c++) {
            const dur = time[candidates[c].end] - time[candidates[c].start];
            const diff = Math.abs(dur - flightTimeSec);
            if (diff < bestDiff) { bestDiff = diff; bestIdx = c; }
        }
        flightStart = candidates[bestIdx].start;
        flightEnd = candidates[bestIdx].end;
    }

    // 2) Peak force: highest force before flight
    let peakForceIdx = 0;
    let peakForce = 0;
    for (let i = 0; i < flightStart; i++) {
        const f = combined[i];
        if (f != null && f > peakForce) {
            peakForce = f;
            peakForceIdx = i;
        }
    }

    // 3) Eccentric start: prefer Hawkin's time_to_takeoff (works backwards from
    //    takeoff), fall back to force-threshold detection.
    let eccentricStart = -1;
    if (timeToTakeoff && timeToTakeoff > 0) {
        const eccStartT = time[flightStart] - timeToTakeoff;
        let best = 0;
        for (let i = 1; i < flightStart; i++) {
            if (Math.abs(time[i] - eccStartT) < Math.abs(time[best] - eccStartT)) best = i;
        }
        eccentricStart = best;
    } else {
        const departThreshold = bw * 0.05;
        for (let i = 0; i < peakForceIdx; i++) {
            const f = combined[i];
            if (f != null && f < bw - departThreshold) {
                eccentricStart = i;
                break;
            }
        }
    }
    if (eccentricStart < 0) eccentricStart = 0;

    // 4) Landing end: after flightEnd, find where force settles back near BW
    let landingEnd = Math.min(flightEnd + 1000, combined.length - 1);
    const settleThreshold = bw * 0.15;
    for (let i = flightEnd + 50; i < combined.length; i++) {
        const f = combined[i];
        if (f != null && Math.abs(f - bw) < settleThreshold) {
            // Check a small window ahead to confirm it's actually settling
            let settled = true;
            for (let j = i; j < Math.min(i + 30, combined.length); j++) {
                const fj = combined[j];
                if (fj != null && Math.abs(fj - bw) > settleThreshold * 2) {
                    settled = false;
                    break;
                }
            }
            if (settled) { landingEnd = i; break; }
        }
    }

    return { eccentricStart, peakForceIdx, peakForce, flightStart, flightEnd, landingEnd };
}

const ForceTimeCurve: React.FC<ForceTimeCurveProps> = ({ testId, onClose }) => {
    const { isDark } = useTheme();
    const [data, setData] = useState<ForceTimeData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showLeftRight, setShowLeftRight] = useState(true);
    const [overlay, setOverlay] = useState<OverlayChannel | null>(null);
    const [pinnedMetrics, setPinnedMetrics] = useState<Set<string>>(new Set());
    const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);
    const chartRef = useRef<any>(null);

    const activeMetrics = useMemo(() => {
        const s = new Set(pinnedMetrics);
        if (hoveredMetric) s.add(hoveredMetric);
        return s;
    }, [pinnedMetrics, hoveredMetric]);

    const isMetricActive = (label: string) => activeMetrics.has(label);

    useEffect(() => {
        setLoading(true);
        setError('');
        authenticatedFetch(getApiUrl(`/api/hawkin/forcetime/${testId}/`), {}, 'Force-Time Curve')
            .then((result) => {
                if (!result.success) throw new Error(result.error || 'Request failed');
                setData(result.data);
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [testId]);

    const phases = useMemo(() => {
        if (!data) return null;
        return findPhases(
            data.time_s, data.combined_force_n, data.test_meta.system_weight_n,
            data.metrics.time_to_takeoff_s, data.metrics.flight_time_s,
        );
    }, [data]);

    /** Braking/propulsive phase index ranges derived from stored durations + velocity data. */
    const lrPhases = useMemo(() => {
        if (!data || !phases) return null;
        const { time_s: t, velocity_m_s: vel, left_force_n: lf, right_force_n: rf, metrics: m } = data;
        const brakingDur = m.braking_phase_s as number | null;
        const propDur = m.propulsive_phase_s as number | null;
        if (!brakingDur || !propDur) return null;

        const flightT = t[phases.flightStart];
        const propStartT = flightT - propDur;
        const brakeStartT = propStartT - brakingDur;

        const nearest = (target: number) => {
            let best = 0;
            for (let i = 1; i < t.length; i++) {
                if (Math.abs(t[i] - target) < Math.abs(t[best] - target)) best = i;
            }
            return best;
        };

        const brakeStart = nearest(brakeStartT);
        const brakeEnd = nearest(propStartT);
        const propEnd = phases.flightStart;

        const avgInRange = (arr: (number | null)[], s: number, e: number) => {
            let sum = 0, n = 0;
            for (let i = s; i <= Math.min(e, arr.length - 1); i++) {
                if (arr[i] != null) { sum += arr[i]!; n++; }
            }
            return n > 0 ? sum / n : 0;
        };

        return {
            brakeStart, brakeEnd, propStart: brakeEnd, propEnd,
            brakeLAvg: avgInRange(lf, brakeStart, brakeEnd),
            brakeRAvg: avgInRange(rf, brakeStart, brakeEnd),
            propLAvg: avgInRange(lf, brakeEnd, propEnd),
            propRAvg: avgInRange(rf, brakeEnd, propEnd),
        };
    }, [data, phases]);

    if (loading) {
        return (
            <div className="cgm-card p-6">
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="cgm-card p-6">
                <div className="flex items-center justify-between mb-4">
                    <span className="text-sm cgm-text-secondary">Force-Time Curve</span>
                    {onClose && (
                        <button onClick={onClose} className="cgm-text-secondary hover:cgm-text-primary text-lg leading-none">&times;</button>
                    )}
                </div>
                <p className="text-sm text-red-500">{error || 'No data available.'}</p>
            </div>
        );
    }

    const { time_s, left_force_n, right_force_n, combined_force_n, has_left, has_right, velocity_m_s, displacement_m, power_w, test_meta, metrics } = data;

    const dualPlate = has_left && has_right;
    const singlePlateLabel = has_left ? 'Left Force (N)' : has_right ? 'Right Force (N)' : 'Force (N)';

    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tickColor = isDark ? '#9CA3AF' : '#6B7280';
    const bw = test_meta.system_weight_n;

    const titleParts = [test_meta.athlete_name, test_meta.test_type_name];
    if (test_meta.trial_label) titleParts.push(test_meta.trial_label);
    const title = titleParts.join(' — ');

    const phaseColor = (c: string, a: number) => {
        const hex = c.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r},${g},${b},${a})`;
    };

    const phaseAnnotations: Record<string, any> = {};
    if (phases) {
        const t = time_s;

        // Eccentric Phase: from first departure from BW to peak force
        phaseAnnotations['eccentric'] = {
            type: 'box',
            xMin: t[phases.eccentricStart],
            xMax: t[phases.peakForceIdx],
            backgroundColor: phaseColor('#3B82F6', 0.08),
            borderWidth: 0,
            label: { display: true, content: 'Eccentric Phase', position: 'start', font: { size: 10 }, color: tickColor },
        };

        // Concentric Phase: from peak force to takeoff (flight start)
        phaseAnnotations['concentric'] = {
            type: 'box',
            xMin: t[phases.peakForceIdx],
            xMax: t[phases.flightStart],
            backgroundColor: phaseColor('#10B981', 0.08),
            borderWidth: 0,
            label: { display: true, content: 'Concentric Phase', position: 'start', font: { size: 10 }, color: tickColor },
        };

        // Flight Phase
        phaseAnnotations['flight'] = {
            type: 'box',
            xMin: t[phases.flightStart],
            xMax: t[phases.flightEnd],
            backgroundColor: phaseColor('#F59E0B', 0.08),
            borderWidth: 0,
            label: { display: true, content: 'Flight Phase', position: 'start', font: { size: 10 }, color: tickColor },
        };

        // Landing Phase
        phaseAnnotations['landing'] = {
            type: 'box',
            xMin: t[phases.flightEnd],
            xMax: t[phases.landingEnd],
            backgroundColor: phaseColor('#8B5CF6', 0.08),
            borderWidth: 0,
            label: { display: true, content: 'Landing Phase', position: 'start', font: { size: 10 }, color: tickColor },
        };

        // === Metric-specific annotations (shown on hover/click of metric cards) ===

        const peakInRange = (arr: (number | null)[], s: number, e: number) => {
            let maxV = -Infinity, maxI = s;
            for (let i = s; i <= Math.min(e, arr.length - 1); i++) {
                const v = arr[i];
                if (v != null && v > maxV) { maxV = v; maxI = i; }
            }
            return { idx: maxI, val: maxV === -Infinity ? 0 : maxV };
        };

        const minInRange = (arr: (number | null)[], s: number, e: number) => {
            let minV = Infinity, minI = s;
            for (let i = s; i <= Math.min(e, arr.length - 1); i++) {
                const v = arr[i];
                if (v != null && v < minV) { minV = v; minI = i; }
            }
            return { idx: minI, val: minV === Infinity ? 0 : minV };
        };

        const labelBg = isDark ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.9)';

        const addDotLabel = (
            prefix: string, xVal: number, yVal: number,
            text: string, dotColor: string, textColor: string,
            yAdj = -24, xAdj = 10,
        ) => {
            phaseAnnotations[`${prefix}Label`] = {
                type: 'label', xValue: xVal, yValue: yVal,
                content: text,
                font: { size: 12, weight: 'bold' as const },
                color: textColor, backgroundColor: labelBg,
                padding: { top: 4, bottom: 4, left: 8, right: 8 },
                borderRadius: 4, yAdjust: yAdj, xAdjust: xAdj,
            };
            phaseAnnotations[`${prefix}Dot`] = {
                type: 'point', xValue: xVal, yValue: yVal,
                backgroundColor: dotColor, borderColor: '#fff',
                borderWidth: 2, radius: 6,
            };
        };

        const addBracket = (
            prefix: string, xStart: number, xEnd: number,
            color: string, text: string, textColor: string,
        ) => {
            const ref = (bw || 800);
            const barY = ref * -0.3;
            const capTop = ref * -0.15;
            // Crossbar (horizontal, below zero)
            phaseAnnotations[`${prefix}Line`] = {
                type: 'line', xMin: xStart, xMax: xEnd,
                yMin: barY, yMax: barY,
                borderColor: color, borderWidth: 2,
            };
            // Left cap (vertical, from crossbar up past zero)
            phaseAnnotations[`${prefix}CapL`] = {
                type: 'line', xMin: xStart, xMax: xStart,
                yMin: barY, yMax: capTop,
                borderColor: color, borderWidth: 2,
            };
            // Right cap (vertical, from crossbar up past zero)
            phaseAnnotations[`${prefix}CapR`] = {
                type: 'line', xMin: xEnd, xMax: xEnd,
                yMin: barY, yMax: capTop,
                borderColor: color, borderWidth: 2,
            };
            // Label below the crossbar
            phaseAnnotations[`${prefix}Lbl`] = {
                type: 'label',
                xValue: (xStart + xEnd) / 2,
                yValue: barY,
                yAdjust: 18,
                content: text,
                font: { size: 12, weight: 'bold' as const },
                color: textColor, backgroundColor: labelBg,
                padding: { top: 4, bottom: 4, left: 8, right: 8 },
                borderRadius: 4,
            };
        };

        // 1. Peak Force — dot + label at curve peak
        if (isMetricActive('Peak Force')) {
            const display = metrics.peak_propulsive_force_n ?? phases.peakForce;
            addDotLabel('peakForce', t[phases.peakForceIdx], phases.peakForce,
                `Peak Force: ${display.toFixed(0)} N`, '#EF4444',
                isDark ? '#F87171' : '#DC2626');
        }

        // 2. Flight Time — bracket under the flight-phase flatline
        if (isMetricActive('Flight Time') && metrics.flight_time_s != null) {
            addBracket('flightTime', t[phases.flightStart], t[phases.flightEnd],
                '#F59E0B', `Flight Time: ${metrics.flight_time_s.toFixed(3)} s`,
                isDark ? '#FBBF24' : '#B45309');
        }

        // 3. Time to Takeoff — bracket spanning eccentric + concentric
        if (isMetricActive('Time to Takeoff') && metrics.time_to_takeoff_s != null) {
            addBracket('tto', t[phases.eccentricStart], t[phases.flightStart],
                '#6366F1', `Time to Takeoff: ${metrics.time_to_takeoff_s.toFixed(3)} s`,
                isDark ? '#A5B4FC' : '#4338CA');
        }

        // RSI — show both Flight Time and Time to Takeoff brackets + division label
        if (isMetricActive('RSI') && metrics.rsi != null
            && metrics.flight_time_s != null && metrics.time_to_takeoff_s != null) {
            addBracket('rsiFlight', t[phases.flightStart], t[phases.flightEnd],
                '#F59E0B', `${metrics.flight_time_s.toFixed(3)} s`,
                isDark ? '#FBBF24' : '#B45309');
            addBracket('rsiTTO', t[phases.eccentricStart], t[phases.flightStart],
                '#6366F1', `${metrics.time_to_takeoff_s.toFixed(3)} s`,
                isDark ? '#A5B4FC' : '#4338CA');
            const midX = (t[phases.eccentricStart] + t[phases.flightEnd]) / 2;
            const peakY = phases.peakForce;
            phaseAnnotations['rsiLabel'] = {
                type: 'label',
                xValue: midX,
                yValue: peakY,
                yAdjust: 0,
                content: `${metrics.flight_time_s.toFixed(3)} / ${metrics.time_to_takeoff_s.toFixed(3)} = ${metrics.rsi.toFixed(3)}`,
                font: { size: 13, weight: 'bold' as const },
                color: isDark ? '#5EEAD4' : '#0F766E',
                backgroundColor: labelBg,
                padding: { top: 5, bottom: 5, left: 10, right: 10 },
                borderRadius: 4,
            };
        }

        // 4. Peak Landing Force — dot + label below the peak in landing phase
        if (isMetricActive('Peak Landing Force')) {
            const lp = peakInRange(combined_force_n, phases.flightEnd, phases.landingEnd);
            const display = metrics.peak_landing_force_n ?? lp.val;
            addDotLabel('peakLanding', t[lp.idx], lp.val,
                `Peak Landing: ${display.toFixed(0)} N`, '#8B5CF6',
                isDark ? '#C4B5FD' : '#6D28D9', 28, 10);
        }

        // 5. Braking RFD — bold combined force during braking + slope label
        if (isMetricActive('Braking RFD') && lrPhases && metrics.braking_rfd_n_s != null) {
            const bsIdx = lrPhases.brakeStart;
            const beIdx = lrPhases.brakeEnd;
            const fStart = combined_force_n[bsIdx] ?? 0;
            const fEnd = combined_force_n[beIdx] ?? 0;
            const dur = t[beIdx] - t[bsIdx];

            addDotLabel('rfdStart', t[bsIdx], fStart,
                `${fStart.toFixed(0)} N`, '#3B82F6',
                isDark ? '#93C5FD' : '#1D4ED8', 24, 0);

            addDotLabel('rfdEnd', t[beIdx], fEnd,
                `${fEnd.toFixed(0)} N`, '#3B82F6',
                isDark ? '#93C5FD' : '#1D4ED8', -24, 0);

            const midT = (t[bsIdx] + t[beIdx]) / 2;
            phaseAnnotations['rfdLabel'] = {
                type: 'label', xValue: midT, yValue: phases.peakForce,
                xAdjust: -100,
								yAdjust: 50,
                content: `${metrics.braking_rfd_n_s.toFixed(0)} N/s  (Δ${(fEnd - fStart).toFixed(0)} N / ${dur.toFixed(3)} s)`,
                font: { size: 11, weight: 'bold' as const },
                color: isDark ? '#93C5FD' : '#1D4ED8', backgroundColor: labelBg,
                padding: { top: 4, bottom: 4, left: 8, right: 8 }, borderRadius: 4,
            };
        }

        // 6. L/R Braking Force — combined avg force label during braking phase
        if (isMetricActive('L/R Braking Force') && dualPlate && lrPhases) {
            const midT = (t[lrPhases.brakeStart] + t[lrPhases.brakeEnd]) / 2;
            phaseAnnotations['brakeAvg'] = {
                type: 'label', xValue: midT, yValue: phases.peakForce,
                yAdjust: 0,
                content: `L: ${lrPhases.brakeLAvg.toFixed(0)} N  |  R: ${lrPhases.brakeRAvg.toFixed(0)} N`,
                font: { size: 11, weight: 'bold' as const },
                color: isDark ? '#D1D5DB' : '#374151', backgroundColor: labelBg,
                padding: { top: 4, bottom: 4, left: 8, right: 8 }, borderRadius: 4,
            };
        }

        // 6. L/R Prop. Force — combined avg force label during propulsive phase
        if (isMetricActive('L/R Prop. Force') && dualPlate && lrPhases) {
            const midT = (t[lrPhases.propStart] + t[lrPhases.propEnd]) / 2;
            phaseAnnotations['propAvg'] = {
                type: 'label', xValue: midT, yValue: phases.peakForce,
                yAdjust: 0,
                content: `L: ${lrPhases.propLAvg.toFixed(0)} N  |  R: ${lrPhases.propRAvg.toFixed(0)} N`,
                font: { size: 11, weight: 'bold' as const },
                color: isDark ? '#D1D5DB' : '#374151', backgroundColor: labelBg,
                padding: { top: 4, bottom: 4, left: 8, right: 8 }, borderRadius: 4,
            };
        }

        // 7. Jump Height — label centered in flight phase
        if (isMetricActive('Jump Height') && metrics.jump_height_m != null) {
            phaseAnnotations['jumpHeight'] = {
                type: 'label',
                xValue: (t[phases.flightStart] + t[phases.flightEnd]) / 2,
                yValue: (bw || 800) * 0.35,
                content: `\u2191 ${metrics.jump_height_m.toFixed(3)} m`,
                font: { size: 13, weight: 'bold' as const },
                color: isDark ? '#34D399' : '#059669',
                backgroundColor: labelBg,
                padding: { top: 6, bottom: 6, left: 10, right: 10 },
                borderRadius: 4,
            };
        }

        // 8. CM Depth — dot at min force during countermovement
        if (isMetricActive('CM Depth') && metrics.countermovement_depth_m != null) {
            const minF = minInRange(combined_force_n, phases.eccentricStart, phases.peakForceIdx);
            addDotLabel('cmDepth', t[minF.idx], minF.val,
                `CM Depth: ${metrics.countermovement_depth_m.toFixed(3)} m`, '#14B8A6',
                isDark ? '#5EEAD4' : '#0F766E');
        }

        // 9. Takeoff Velocity — dot at the takeoff point
        if (isMetricActive('Takeoff Vel.') && metrics.takeoff_velocity_m_s != null) {
            const toIdx = Math.max(0, phases.flightStart - 1);
            const forceAtTO = combined_force_n[toIdx] ?? 0;
            addDotLabel('takeoffVel', t[toIdx], forceAtTO,
                `Takeoff: ${metrics.takeoff_velocity_m_s.toFixed(2)} m/s`, '#F59E0B',
                isDark ? '#FDE68A' : '#92400E', 24, 10);
        }

        // 10. Peak Power — dot at peak-power time on force curve
        if (isMetricActive('Peak Power') && metrics.peak_propulsive_power_w != null) {
            const pp = peakInRange(power_w, phases.eccentricStart, phases.flightStart);
            if (pp.val > 0) {
                const forceAtPP = combined_force_n[pp.idx] ?? 0;
                addDotLabel('peakPower', t[pp.idx], forceAtPP,
                    `Peak Power: ${metrics.peak_propulsive_power_w.toFixed(0)} W`, '#A855F7',
                    isDark ? '#C4B5FD' : '#6D28D9', -24, -10);
            }
        }
    }

    // Body weight line (always show if available)
    if (bw) {
        phaseAnnotations['bodyWeight'] = {
            type: 'line',
            yMin: bw,
            yMax: bw,
            borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
            borderWidth: 1,
            borderDash: [6, 4],
            label: {
                display: true,
                content: `Body Weight: ${bw.toFixed(0)} N`,
                position: 'start',
                font: { size: 10 },
                color: tickColor,
                backgroundColor: 'transparent',
            },
        };
    }

    const datasets: any[] = [];

    const brakingRfdActive = isMetricActive('Braking RFD') && lrPhases;

    if (dualPlate) {
        // Both plates present — show combined as primary, L/R as optional overlays
        const combinedSegment = brakingRfdActive ? {
            borderWidth: (ctx: any) => (ctx.p0DataIndex >= lrPhases!.brakeStart && ctx.p0DataIndex <= lrPhases!.brakeEnd) ? 3.5 : 2,
            borderColor: (ctx: any) => (ctx.p0DataIndex >= lrPhases!.brakeStart && ctx.p0DataIndex <= lrPhases!.brakeEnd) ? '#3B82F6' : phaseColor('#3B82F6', 0.35),
            borderDash: (ctx: any) => (ctx.p0DataIndex >= lrPhases!.brakeStart && ctx.p0DataIndex <= lrPhases!.brakeEnd) ? [] : [4, 2],
        } : undefined;
        datasets.push({
            label: 'Combined Force (N)',
            data: combined_force_n,
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59,130,246,0.08)',
            fill: true,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            tension: 0,
            yAxisID: 'y',
            segment: combinedSegment,
        });

        if (showLeftRight) {
            const brakingActive = isMetricActive('L/R Braking Force') && lrPhases;
            const propActive = isMetricActive('L/R Prop. Force') && lrPhases;

            const inActivePhase = (idx: number) => {
                if (!lrPhases) return false;
                if (brakingActive && idx >= lrPhases.brakeStart && idx <= lrPhases.brakeEnd) return true;
                if (propActive && idx >= lrPhases.propStart && idx <= lrPhases.propEnd) return true;
                return false;
            };

            const segmentStyle = (color: string) => ({
                borderWidth: (ctx: any) => inActivePhase(ctx.p0DataIndex) ? 2.8 : 1.2,
                borderDash: (ctx: any) => inActivePhase(ctx.p0DataIndex) ? [] : [4, 2],
                borderColor: (ctx: any) => inActivePhase(ctx.p0DataIndex) ? color : phaseColor(color, 0.4),
            });

            datasets.push(
                {
                    label: 'Left Force (N)',
                    data: left_force_n,
                    borderColor: '#10B981',
                    borderWidth: 1.2,
                    pointRadius: 0,
                    pointHoverRadius: 2,
                    tension: 0,
                    borderDash: [4, 2],
                    yAxisID: 'y',
                    segment: (brakingActive || propActive) ? segmentStyle('#10B981') : undefined,
                },
                {
                    label: 'Right Force (N)',
                    data: right_force_n,
                    borderColor: '#EF4444',
                    borderWidth: 1.2,
                    pointRadius: 0,
                    pointHoverRadius: 2,
                    tension: 0,
                    borderDash: [4, 2],
                    yAxisID: 'y',
                    segment: (brakingActive || propActive) ? segmentStyle('#EF4444') : undefined,
                },
            );
        }
    } else {
        // Single plate — use the plate's canonical color (Left = green, Right = red)
        const singleData = has_left ? left_force_n : right_force_n;
        const singleColor = has_left ? '#10B981' : '#EF4444';
        datasets.push({
            label: singlePlateLabel,
            data: singleData.length > 0 ? singleData : combined_force_n,
            borderColor: singleColor,
            backgroundColor: phaseColor(singleColor, 0.08),
            fill: true,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            tension: 0,
            yAxisID: 'y',
        });
    }

    const scales: Record<string, any> = {
        x: {
            type: 'linear' as const,
            title: { display: true, text: 'Time (s)', color: tickColor },
            ticks: { color: tickColor, maxTicksLimit: 20 },
            grid: { color: gridColor },
        },
        y: {
            type: 'linear' as const,
            position: 'left' as const,
            title: { display: true, text: 'Force (N)', color: tickColor },
            ticks: { color: tickColor },
            grid: { color: gridColor },
            suggestedMin: -(bw || 800) * 0.35,
        },
    };

    if (overlay) {
        const cfg = OVERLAY_CONFIG[overlay];
        const channelData =
            overlay === 'velocity' ? velocity_m_s :
            overlay === 'power' ? power_w :
            displacement_m;

        datasets.push({
            label: `${cfg.label} (${cfg.unit})`,
            data: channelData,
            borderColor: cfg.color,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 2,
            tension: 0,
            yAxisID: 'y2',
        });

        scales['y2'] = {
            type: 'linear' as const,
            position: 'right' as const,
            title: { display: true, text: `${cfg.label} (${cfg.unit})`, color: cfg.color },
            ticks: { color: cfg.color },
            grid: { drawOnChartArea: false },
        };
    }

    const chartData = {
        labels: time_s,
        datasets,
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false as any,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: { color: tickColor, usePointStyle: true, pointStyle: 'line', boxWidth: 30, font: { size: 11 } },
            },
            tooltip: {
                callbacks: {
                    title: (items) => items[0] ? `t = ${Number(items[0].label).toFixed(3)} s` : '',
                },
            },
            annotation: {
                annotations: phaseAnnotations,
            },
        },
        scales,
    };

    const metricCards = [
        { label: 'Jump Height', value: metrics.jump_height_m, unit: 'm', fmt: 3, hasAnnotation: true },
        { label: 'Peak Force', value: metrics.peak_propulsive_force_n, unit: 'N', fmt: 0, hasAnnotation: true },
        { label: 'RSI', value: metrics.rsi, unit: '', fmt: 3, hasAnnotation: true },
        { label: 'Takeoff Vel.', value: metrics.takeoff_velocity_m_s, unit: 'm/s', fmt: 2, hasAnnotation: true },
        { label: 'Flight Time', value: metrics.flight_time_s, unit: 's', fmt: 3, hasAnnotation: true },
        { label: 'Peak Power', value: metrics.peak_propulsive_power_w, unit: 'W', fmt: 0, hasAnnotation: true },
        { label: 'CM Depth', value: metrics.countermovement_depth_m, unit: 'm', fmt: 3, hasAnnotation: true },
        { label: 'Time to Takeoff', value: metrics.time_to_takeoff_s, unit: 's', fmt: 3, hasAnnotation: true },
        { label: 'Braking RFD', value: metrics.braking_rfd_n_s, unit: 'N/s', fmt: 0, hasAnnotation: true },
        { label: 'L/R Prop. Force', value: metrics.lr_avg_propulsive_force_pct, unit: '%', fmt: 1, hasAnnotation: true },
        { label: 'L/R Braking Force', value: metrics.lr_avg_braking_force_pct, unit: '%', fmt: 1, hasAnnotation: true },
        { label: 'Peak Landing Force', value: metrics.peak_landing_force_n, unit: 'N', fmt: 0, hasAnnotation: true },
    ].filter((m) => m.value != null);

    return (
        <div className="cgm-card p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-base font-semibold cgm-text-primary">
                        {title}
                    </h3>
                    <p className="text-xs cgm-text-secondary">
                        {test_meta.date} &middot; {data.sample_count.toLocaleString()} samples
                        {!dualPlate && <span> &middot; Single plate ({has_left ? 'Left' : 'Right'})</span>}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {dualPlate && (
                        <label className="flex items-center gap-1.5 text-xs cgm-text-secondary cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showLeftRight}
                                onChange={(e) => setShowLeftRight(e.target.checked)}
                                className=""
                            />
                            L / R plates
                        </label>
                    )}
                    <select
                        value={overlay || ''}
                        onChange={(e) => setOverlay((e.target.value || null) as OverlayChannel | null)}
                        className="cgm-input text-xs py-1 px-2"
                    >
                        <option value="">No overlay</option>
                        <option value="velocity">Velocity</option>
                        <option value="power">Power</option>
                        <option value="displacement">Displacement</option>
                    </select>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="cgm-text-secondary hover:cgm-text-primary text-lg leading-none ml-2"
                            aria-label="Close"
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>

            {/* Chart */}
            <div style={{ height: 420 }}>
                <Line ref={chartRef} data={chartData} options={options} />
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {metricCards.map((m) => {
                    const isActive = isMetricActive(m.label);
                    const isClickable = m.hasAnnotation;
                    const tip = METRIC_TOOLTIPS[m.label];
                    return (
                        <div
                            key={m.label}
                            className={`relative p-3 text-center transition-all duration-150 ${
                                isActive
                                    ? 'cgm-card-selected border border-[var(--card-selected-border)]'
                                    : 'bg-[var(--bg-card)] border border-[var(--card-border)]'
                            } ${isClickable ? 'cursor-pointer hover:border-[var(--card-hover-border)]' : ''}`}
                            onClick={() => {
                                if (!isClickable) return;
                                setPinnedMetrics((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(m.label)) next.delete(m.label);
                                    else next.add(m.label);
                                    return next;
                                });
                            }}
                            onMouseEnter={() => { if (isClickable) setHoveredMetric(m.label); }}
                            onMouseLeave={() => setHoveredMetric(null)}
                        >
                            {tip && (
                                <div
                                    className="group/tip absolute top-1.5 right-1.5 z-10"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Info size={13} className="cgm-text-secondary opacity-40 hover:opacity-100 transition-opacity cursor-help" />
                                    <div className={`hidden group-hover/tip:block absolute right-0 top-full mt-1 ${tip.link ? 'w-72' : 'w-56'} bg-gray-900 dark:bg-gray-700 text-white text-xs leading-relaxed p-3 shadow-lg ${tip.link ? '' : 'pointer-events-none'}`}>
                                        <p className="font-semibold mb-1">{tip.fullName}</p>
                                        <p className="opacity-90">{tip.description}</p>
                                        {tip.link && (
                                            <a href={tip.link} target="_blank" rel="noopener noreferrer"
                                               className="inline-block mt-2 text-blue-300 hover:text-blue-200 underline pointer-events-auto">
                                                Read more →
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                            <p className="text-xs cgm-text-secondary mb-1">{m.label}</p>
                            <p className="text-lg font-semibold cgm-text-primary">
                                {m.label === 'L/R Prop. Force' || m.label === 'L/R Braking Force' ? (
                                    formatLrAsymmetryPct(m.value as number)
                                ) : (
                                    <>
                                        {(m.value as number).toFixed(m.fmt)}
                                        {m.unit && <span className="text-xs cgm-text-secondary ml-0.5">{m.unit}</span>}
                                    </>
                                )}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ForceTimeCurve;
